import * as i0 from '@angular/core';
import { HostListener, Directive, inject, DestroyRef, signal, computed } from '@angular/core';
import { createStateToken, createStateProvider, createStateInjector, createState } from 'ng-primitives/state';
import { safeTakeUntilDestroyed } from 'ng-primitives/utils';
import { fromEvent } from 'rxjs';

/**
 * The state token  for the Search primitive.
 */
const NgpSearchStateToken = createStateToken('Search');
/**
 * Provides the Search state.
 */
const provideSearchState = createStateProvider(NgpSearchStateToken);
/**
 * Injects the Search state.
 */
const injectSearchState = createStateInjector(NgpSearchStateToken);
/**
 * The Search state registration function.
 */
const searchState = createState(NgpSearchStateToken);

/**
 * The `NgpSearchClear` directive is can be added to a button to clear the search field on click.
 */
let NgpSearchClear = /*#__PURE__*/(() => {
  class NgpSearchClear {
    constructor() {
      /**
       * Access the Search instance.
       */
      this.search = injectSearchState();
    }
    /**
     * Clear the input field.
     */
    clear() {
      this.search().clear();
    }
    static {
      this.ɵfac = function NgpSearchClear_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpSearchClear)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpSearchClear,
        selectors: [["", "ngpSearchClear", ""]],
        hostVars: 2,
        hostBindings: function NgpSearchClear_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function NgpSearchClear_click_HostBindingHandler() {
              return ctx.clear();
            });
          }
          if (rf & 2) {
            i0.ɵɵhostProperty("tabindex", -1);
            i0.ɵɵattribute("data-empty", ctx.search().empty() ? "" : null);
          }
        },
        exportAs: ["ngpSearchClear"]
      });
    }
  }
  return NgpSearchClear;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * The `NgpSearch` directive is a container for the search field components.
 */
let NgpSearch = /*#__PURE__*/(() => {
  class NgpSearch {
    constructor() {
      /**
       * The destroy reference.
       */
      this.destroyRef = inject(DestroyRef);
      /**
       * The input field.
       */
      this.input = signal(null);
      /**
       * The value of the input.
       */
      this.value = signal('');
      /**
       * Whether the input field is empty.
       * @internal
       */
      this.empty = computed(() => this.value() === '');
      /**
       * The search field state.
       */
      this.state = searchState(this);
    }
    clear() {
      const input = this.input();
      if (!input) {
        return;
      }
      input.value = '';
      input.dispatchEvent(new Event('input', {
        bubbles: true
      }));
    }
    /**
     * Register the input field.
     * @param input The input field.
     * @internal
     */
    registerInput(input) {
      this.input.set(input);
      this.value.set(input.value);
      fromEvent(input, 'input').pipe(safeTakeUntilDestroyed(this.destroyRef)).subscribe(() => this.value.set(input.value));
    }
    static {
      this.ɵfac = function NgpSearch_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgpSearch)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgpSearch,
        selectors: [["", "ngpSearch", ""]],
        hostVars: 1,
        hostBindings: function NgpSearch_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown.escape", function NgpSearch_keydown_escape_HostBindingHandler() {
              return ctx.clear();
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("data-empty", ctx.empty() ? "" : null);
          }
        },
        exportAs: ["ngpSearch"],
        features: [i0.ɵɵProvidersFeature([provideSearchState()])]
      });
    }
  }
  return NgpSearch;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NgpSearch, NgpSearchClear, injectSearchState, provideSearchState };
